import React from 'react';
import { Text, Container, Sticky, Link, Message } from 'basis';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';

import { Header } from 'components/header/Header';
import { Footer } from 'components/footer/Footer';
import { YellowMessage } from 'components/message/YellowMessage';
import { config, PRODUCT_CODE_DAVID_JONES_PREMIERE } from '_config';
import { getFeatureValue } from 'featureToggles';
import { isDJ } from 'utils/byConfig';
import DJCardsImg from 'images/banner/dj-maintenance-cards.jpg';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
  width: 100%;
`;

const ContentWrapper = styled.div`
  max-width: 600px;
  min-height: 500px;
  margin: ${props => (props.margin ? props.margin : '16px')};
`;

// DJ hero per design (1440 frame): 360px tall, image area 35% (505px) on the left,
// grey panel starts at ~32.85% (473px) so it overlaps the image with a 120px bottom-left radius.
const BannerPage = styled.div`
  background: white;
  padding-bottom: 100px;

  @media (max-width: ${props => props.theme.screenSize.md}) {
    padding-bottom: 48px;
  }
`;

// Full viewport height column so the footer sits at the very bottom with no white gap below it.
const FullHeightPage = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: white;

  & > :first-child {
    flex: 1 0 auto;
  }
`;

const DJBannerWrapper = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 360px;
  overflow: hidden;
  background: black;

  @media (max-width: ${props => props.theme.screenSize.md}) {
    flex-direction: column;
    height: auto;
  }
`;

const DJBannerImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 35%;
  height: 100%;
  overflow: hidden;

  & > img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: left center;
  }

  @media (max-width: ${props => props.theme.screenSize.md}) {
    position: static;
    width: 100%;
    height: auto;

    & > img {
      height: auto;
    }
  }
`;

const DJBannerContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 67.15%;
  margin-left: 32.85%;
  padding: 40px 96px;
  box-sizing: border-box;
  background: linear-gradient(180deg, #5c5c5c 0%, #8e8e8e 100%);
  border-radius: 0 0 0 120px;

  @media (max-width: ${props => props.theme.screenSize.lg}) {
    padding: 32px 48px;
    border-radius: 0 0 0 80px;
  }

  @media (max-width: ${props => props.theme.screenSize.md}) {
    width: 100%;
    margin-left: 0;
    padding: 32px 24px 40px;
    border-radius: 0;
  }
`;

const DJBannerTitle = styled.h1`
  margin: 0 0 32px;
  max-width: 680px;
  letter-spacing: 0.2px;
  color: white;
  font-family: 'Latitude Sans', 'Montserrat', sans-serif;
  font-size: 40px;
  font-weight: 500;
  line-height: 1.15;

  @media (max-width: ${props => props.theme.screenSize.lg}) {
    font-size: 30px;
  }

  @media (max-width: ${props => props.theme.screenSize.sm}) {
    font-size: 26px;
    margin-bottom: 24px;
  }
`;

const DJBannerButton = styled.a`
  display: inline-block;
  padding: 12px 18px;
  border: 2px solid white;
  border-radius: 4px;
  color: white;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  text-decoration: none;

  &:hover,
  &:focus {
    background: rgba(255, 255, 255, 0.15);
    color: white;
  }
`;

// Only allow https links from the flag
const isHttpsLink = link => /^https:\/\//i.test(link || '');

function DJMaintenanceBanner({ notice }) {
  const theme = useTheme();

  return (
    <DJBannerWrapper theme={theme} data-testid="dj-maintenance-banner">
      <DJBannerImage theme={theme}>
        <img src={DJCardsImg} alt="David Jones Premiere and Prestige credit cards" />
      </DJBannerImage>
      <DJBannerContent theme={theme}>
        <DJBannerTitle theme={theme}>{notice.title}</DJBannerTitle>
        {isHttpsLink(notice.link) && (
          <DJBannerButton href={notice.link} target="_blank" rel="noopener noreferrer">
            {(notice.linkText || 'Find out more').replace(/\.$/, '')}
          </DJBannerButton>
        )}
      </DJBannerContent>
    </DJBannerWrapper>
  );
}

function MaintenanceNotice({ notice }) {
  const textColor = isDJ() ? 'indigo' : undefined;

  return (
    <YellowMessage>
      <Message testId="yellow-message" severity="warning-or-significant">
        <Text color={textColor}>
          <b>{notice.title}</b>
        </Text>
        {isHttpsLink(notice.link) && (
          <Text margin="2 0 0 0">
            <Link href={notice.link} newTab>
              {notice.linkText || 'Find out more.'}
            </Link>
          </Text>
        )}
      </Message>
    </YellowMessage>
  );
}

export function Maintenance() {
  const theme = useTheme();
  // DJ pages without a product code (comparison page, QR links) use the Premiere message
  const productCode = config.productCode || (isDJ() ? PRODUCT_CODE_DAVID_JONES_PREMIERE : undefined);
  const message = getFeatureValue('maintenanceMessage')?.[productCode];
  const notice = message?.title ? message : null;

  const Page = notice && isDJ() ? FullHeightPage : React.Fragment;

  return (
    <Page>
      <Sticky>
        <Sticky.Item>
          <Header />
        </Sticky.Item>
        {notice && isDJ() ? (
          <BannerPage>
            <DJMaintenanceBanner notice={notice} />
          </BannerPage>
        ) : (
          <Container padding="12 0" testId="error" hasBreakpointWidth>
            <Wrapper>
              <ContentWrapper>
                {notice ? (
                  <MaintenanceNotice notice={notice} />
                ) : (
                  <>
                    <Container padding="8 0 0 0">
                      <Text as="h5" textStyle="heading5" color={theme.headingColor} align="left">
                        Our application is currently undergoing maintenance
                      </Text>
                      <p hidden>CF flush from inactive is working</p>
                    </Container>

                    <Container padding="8 0">
                      <Text as="p">{typeof message === 'string' ? message : null}</Text>
                    </Container>
                  </>
                )}
              </ContentWrapper>
            </Wrapper>
          </Container>
        )}
      </Sticky>
      <Footer footer="OVERVIEW" />
    </Page>
  );
}
